import React, { useEffect, useState } from 'react';
import {
  GuaranteeCheckboxItem,
  GuaranteeCheckboxHeader,
  GuaranteeCheckboxWrapper,
  GuaranteeParameterContainer,
} from './GuaranteeCard.styles';
import { FormInputSelect } from '../../common/formInputsV2/FormInputSelect';
import {
  commercial_goods,
  commercial_main_risks,
  communal_territories_default_values,
} from './utils';
import { FormInputPrice } from '../../common/formInputsV2/FormInputPrice';
import {
  validateCapitalInput,
  getCapitalLimits,
  validateIncompatibleGuarantees,
} from './validations/validationFunctions';
import { getGuaranteesThatRequire } from './validations/validationHelpers';

function GuaranteeCommercialCard({
  watch,
  name,
  errors,
  isValid,
  register,
  setValue,
  good,
  index,
  isChecked,
  onToggle,
}) {
  const coverages = watch('coverages') || [];
  const attpr_value = watch('attpr_value') || '';
  const site_address = watch('site_address') || '';
  const annoc_value = watch('technical_location_data.annoc_value');
  const province_code = site_address?.province_code || '';
  const zona = communal_territories_default_values[province_code];
  const currentYear = new Date().getFullYear();
  const age = currentYear - annoc_value;
  const building_age = Number.isNaN(age) ? null : age > 5 ? '2' : '1';

  const available_parameter_collections = good.parameter_collections || [];
  const isRCT = good.id === 'RCT';

  const dependents = isChecked
    ? getGuaranteesThatRequire(
      good.id,
      coverages.map((c) => c.coverage_code),
    ).map((code) => commercial_goods.find((g) => g.id === code)?.name || code)
    : [];

  // Real-time capital validation state
  const [capitalValidation, setCapitalValidation] = useState({
    isValid: true,
    error: null,
  });

  // Real-time incompatible guarantees validation state
  const [incompatibilityValidation, setIncompatibilityValidation] = useState({
    isValid: true,
    error: null,
    incompatibleWith: [],
  });

  // Get capital limits for current guarantee (for placeholder hint)
  const capitalLimits = getCapitalLimits(good.id);

  // Validate capital amount when it changes
  useEffect(() => {
    if (!isChecked || good.id === 'RCT' || good.id === 'PROP') return;

    const currentAmount = coverages[index]?.max_amount;
    const validation = validateCapitalInput(good.id, currentAmount);
    setCapitalValidation(validation);
  }, [isChecked, coverages[index]?.max_amount, good.id]);

  // Validate incompatible guarantees when coverages change
  useEffect(() => {
    if (!isChecked) return;

    const allSelectedCodes = coverages
      .map((c) => c.coverage_code)
      .filter(Boolean);
    const validation = validateIncompatibleGuarantees(good.id, allSelectedCodes);
    setIncompatibilityValidation(validation);
  }, [isChecked, coverages, good.id]);

  // Sync max_amount with property_value for specific guarantees based on building type
  useEffect(() => {
    if (!isChecked) return;

    const buildingType = watch('technical_location_data.tpab_value');
    const propertyValue = watch('property_value');

    if (propertyValue) {
      let amountToSet = propertyValue;
      const limits = getCapitalLimits(good.id);

      if (limits) {
        const numValue = parseFloat(propertyValue);
        if (!Number.isNaN(numValue) && numValue < limits.min) {
          amountToSet = limits.min;
        }
        if (!Number.isNaN(numValue) && numValue > limits.max) {
          amountToSet = limits.max;
        }
      }

      switch (good.id) {
        case 'FABB':
          if (buildingType === '07') {
            setValue(`${name}.${index}.max_amount`, amountToSet, {
              shouldTouch: true,
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          break;
        case 'RILO':
          if (buildingType === '08') {
            setValue(`${name}.${index}.max_amount`, amountToSet, {
              shouldTouch: true,
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          break;
        case 'CONT': {
          let cont_value = propertyValue * 0.1;
          const limits2 = getCapitalLimits('CONT');

          if (limits2 && cont_value < limits2.min) {
            cont_value = limits2.min;
          }

          if (limits2 && cont_value > limits2.max) {
            cont_value = limits2.max;
          }

          setValue(`${name}.${index}.max_amount`, cont_value, {
            shouldTouch: true,
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        }

        case 'RTER': {
          let rter_value = propertyValue * 0.1;
          const rter_limits = getCapitalLimits('RTER');

          if (rter_limits && rter_value < rter_limits.min) {
            rter_value = rter_limits.min;
          }

          if (rter_limits && rter_value > rter_limits.max) {
            rter_value = rter_limits.max;
          }

          setValue(`${name}.${index}.max_amount`, rter_value, {
            shouldTouch: true,
            shouldValidate: true,
            shouldDirty: true,
          });
          break;
        }

        default:
          break;
      }
    }
  }, [
    isChecked,
    watch('technical_location_data.tpab_value'),
    watch('property_value'),
    good.id,
    setValue,
    name,
    index,
  ]);

  useEffect(() => {
    if (!isChecked) return;

    available_parameter_collections.forEach(
      (param, parameterCollectionIndex) => {
        const currentValue = coverages[index]?.parameter_collections?.[parameterCollectionIndex]
          ?.parameter_value_code;

        // Get default value from commercial_main_risks or communal_territories
        let defaultValue = currentValue;
        if (!defaultValue && param?.key) {
          if (param.key === 'zona_territoriale' && zona) {
            // For zona territoriale, use the value from communal_territories_default_values
            defaultValue = zona;
          } else if (param.key === 'building_age') {
            // Vetusta' Fabbricato -> derive from technical_location_data.annoc_value
            defaultValue = building_age !== null ? building_age : null;
          } else if (param.key && param.key == 'additional_conditions') {
            defaultValue = param.default_value;
          } else if (attpr_value && good.id) {
            // For other fields with keys, use commercial_main_risks
            const riskData = commercial_main_risks[attpr_value]?.[good.id];
            defaultValue = riskData?.[param.key];
          }
        }

        setValue(
          `${name}.${index}.parameter_collections.${parameterCollectionIndex}`,
          {
            ...coverages[index]?.parameter_collections?.[
              parameterCollectionIndex
            ],
            parameter_sequence: param?.id,
            ...(defaultValue && { parameter_value_code: defaultValue }),
          },
          { shouldTouch: true, shouldValidate: true, shouldDirty: true },
        );
      },
    );
  }, [
    isChecked,
    available_parameter_collections,
    setValue,
    name,
    index,
    coverages,
    attpr_value,
    good.id,
    zona,
  ]);

  // Sync RCT max_amount with the selected parameter value (Massimale)
  const rctMassimaleParamCode = coverages[index]?.parameter_collections?.[0]?.parameter_value_code;
  useEffect(() => {
    if (!isChecked || good.id !== 'RCT' || !rctMassimaleParamCode) return;

    const parameterCollectionIndex = 0;
    const options = available_parameter_collections[parameterCollectionIndex]?.values || [];
    // Use loose equality (==) because rctMassimaleParamCode might be string "2" and option.id might be number 2
    const selectedOption = options.find(
      (opt) => opt.id == rctMassimaleParamCode,
    );

    if (selectedOption?.name) {
      if (selectedOption.id === 5) {
        // Special case for "Massimale Libero": clear for free input
        setValue(`${name}.${index}.max_amount`, '', {
          shouldTouch: true,
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        // Parse "1.000.000,00€" -> "1000000"
        const valStr = selectedOption.name
          .replace(/[€\s]/g, '')
          .replace(/\./g, '')
          .replace(',', '.');

        const num = parseFloat(valStr);
        if (!Number.isNaN(num)) {
          setValue(`${name}.${index}.max_amount`, num, {
            shouldTouch: true,
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    }
  }, [
    isChecked,
    rctMassimaleParamCode,
    good.id,
    setValue,
    name,
    index,
    available_parameter_collections,
  ]);

  // Whole-card red/green: aggregates everything already tracked for this
  // guarantee — incompatibility, real-time capital validation, and any
  // yup/RHF error on this specific coverages[index] item (max_amount,
  // parameter_collections, ...) — plus, for green, that the required
  // inputs actually have a value (an untouched-but-error-free field
  // shouldn't read as "correctly filled").
  const itemErrors = isChecked ? errors?.[name]?.[index] : undefined;
  const hasItemError = !!itemErrors && Object.keys(itemErrors).length > 0;

  const massimaleRequired = good.id !== 'RCT' && good.id !== 'PROP';
  const massimaleFilled = !massimaleRequired || !!coverages[index]?.max_amount;

  const parametersFilled = available_parameter_collections.every((_, paramIndex) => {
    const value = coverages[index]?.parameter_collections?.[paramIndex]?.parameter_value_code;
    return value !== undefined && value !== null && value !== '';
  });

  const cardHasError = isChecked && (
    !incompatibilityValidation.isValid
    || !capitalValidation.isValid
    || hasItemError
  );

  const cardIsValid = isChecked
    && !cardHasError
    && massimaleFilled
    && parametersFilled;

  return (
    <GuaranteeCheckboxItem hasError={cardHasError} hasSuccess={cardIsValid}>
      <GuaranteeCheckboxHeader>
        <GuaranteeCheckboxWrapper
          title={
            isChecked && dependents.length > 0
              ? `Richiesta da: ${dependents.join(', ')}`
              : undefined
          }
        >
          <input
            type="checkbox"
            id={`garanzia-${good.id}`}
            checked={isChecked}
            disabled={isRCT}
            onChange={onToggle}
          />
          <label htmlFor={`garanzia-${good.id}`}>
            {good.name}
            {' '}
            {isRCT && (
              <small>
                (RCO Incluso)
              </small>
            )}
          </label>
        </GuaranteeCheckboxWrapper>
        {isRCT && <span className="badge bg-secondary">Obbligatoria</span>}
      </GuaranteeCheckboxHeader>

      {isChecked && !incompatibilityValidation.isValid && (
        <small className="text-danger">{incompatibilityValidation.error}</small>
      )}

      {isChecked && (
        <GuaranteeParameterContainer>
          <div className="row">
            {available_parameter_collections.length !== 0
              && available_parameter_collections.map(
                (item, parameterCollectionIndex) => {
                  // Check if this field has a default value
                  let defaultValueFromSource = null;

                  if (item?.key === 'zona_territoriale' && zona) {
                    // For zona territoriale, use communal_territories_default_values
                    defaultValueFromSource = zona;
                  } else if (item?.key === 'building_age') {
                    // Vetusta' Fabbricato -> derive from technical_location_data.annoc_value
                    return null;
                  } else if (item.key && item.key == 'additional_conditions') {
                    defaultValueFromSource = item.default_value;
                  } else if (item?.key && attpr_value && good.id) {
                    // For other fields with keys, use commercial_main_risks
                    defaultValueFromSource = commercial_main_risks[attpr_value]?.[good.id]?.[
                      item.key
                    ];
                  }

                  const currentValue = coverages[index]?.parameter_collections?.[
                    parameterCollectionIndex
                  ]?.parameter_value_code;
                  const isDefaultValue = defaultValueFromSource
                    && currentValue === defaultValueFromSource;
                  if (isDefaultValue) {
                    return null;
                  }
                  // The yup schema's message is generic (shared by every
                  // guarantee's parameter selects, whatever they're called),
                  // so it always reads "...il valore del progressivo!" no
                  // matter which field this is. Swap in a message that names
                  // the actual field (item.name — "Massimale", "Categoria
                  // Rischio", ...) instead of surfacing the raw yup text.
                  const parameterError = errors[name]?.[index]?.parameter_collections?.[
                    parameterCollectionIndex
                  ]?.parameter_value_code;

                  return (
                    <div
                      className="col-12 col-md-12"
                      key={parameterCollectionIndex}
                    >
                      <div>
                        <FormInputSelect
                          registration={register(
                            `${name}.${index}.parameter_collections.${parameterCollectionIndex}.parameter_value_code`,
                          )}
                          error={
                            parameterError
                              ? `Il campo è obbligatorio. Seleziona ${item.name}!`
                              : undefined
                          }
                          valid={isValid(
                            `${name}.${index}.parameter_collections.${parameterCollectionIndex}.parameter_value_code`,
                          )}
                          label={item.name}
                          placeholder="- Seleziona -"
                          options={
                            available_parameter_collections[
                              parameterCollectionIndex
                            ]?.values
                          }
                          value={currentValue || ''}
                          disabled={isDefaultValue}
                        />
                      </div>
                    </div>
                  );
                },
              )}
            {good.id !== 'RCT' && good.id !== 'PROP' && (
              <FormInputPrice
                name={`${name}.${index}.max_amount`}
                value={String(coverages[index]?.max_amount || '')}
                placeholder={
                  capitalLimits
                    ? `Min: €${capitalLimits.min.toLocaleString('it-IT')} - Max: €${capitalLimits.max.toLocaleString('it-IT')}`
                    : 'Inserisci importo massimale'
                }
                label="Importo Massimale"
                error={
                  capitalValidation.error
                  || errors[name]?.[index]?.max_amount?.message
                }
                valid={
                  capitalValidation.isValid
                  && isValid(`${name}.${index}.max_amount`)
                }
                onChange={(value) => {
                  setValue(`${name}.${index}.max_amount`, value, {
                    shouldTouch: true,
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  // Real-time validation on change
                  const validation = validateCapitalInput(good.id, value);
                  setCapitalValidation(validation);
                }}
              />
            )}
          </div>
        </GuaranteeParameterContainer>
      )}
    </GuaranteeCheckboxItem>
  );
}

export default GuaranteeCommercialCard;
